import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { Button, Card, ErrorText, Field, Input } from '../lib/ui.tsx';
import { SolfoTile } from '../components/Logo.tsx';

export default function RegisterCoach() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: '', email: '', password: '', dateOfBirth: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string }>('/api/auth/register-coach', {
        body: { ...form, referralCode: params.get('ref') ?? undefined },
      });
      await login(res.token);
      navigate('/coach/onboarding'); // A5
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3">
          <SolfoTile size={64} />
          <h1 className="text-2xl font-medium tracking-tight text-slate-900">
            solfo <span className="text-slate-400">for coaches</span>
          </h1>
        </div>
        <Card>
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Your name">
              <Input value={form.name} onChange={set('name')} required autoFocus />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={set('email')} required />
            </Field>
            <Field label="Password">
              <Input type="password" value={form.password} onChange={set('password')} required minLength={8} />
            </Field>
            <Field label="Date of birth" hint="You must be 18 or older to use Solfo.">
              <Input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} required />
            </Field>
            {params.get('ref') && (
              <p className="rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-800">
                Joining via referral code {params.get('ref')}
              </p>
            )}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? '…' : 'Create account'}
            </Button>
            <ErrorText error={error} />
          </form>
        </Card>
        <p className="mt-4 text-center text-sm text-slate-500">
          Already have an account? <Link to="/login" className="font-medium text-brand-700">Log in</Link>
        </p>
      </div>
    </div>
  );
}
